import { Location } from 'history';
import { useEffect, useRef, useState } from 'react';
import { analyticsCoordinator } from '../lib/analytics/coordinator';
import { hasTargetingConsent } from '../lib/consent';
import { fbInit } from '../lib/analytics/fb';
import hotjar from '../lib/analytics/HotJar';
import { northbeamInit } from '../lib/analytics/northbeam';

/**
 * - loading: Segment's analytics.ready() has not been called yet
 * - waiting: Waiting for Optimize to potentially do its magic in the background.
 *   At this stage, we show the loading indicator but also render the page in the
 *   background, behind the async-hide style.
 * - done: Done.
 */
type AnalyticsState = 'loading' | 'waiting' | 'done';

export default function useAnalytics(location: Location, sessionInitialized: boolean) {
  const [analyticsState, setAnalyticsState] = useState<AnalyticsState>('loading');

  // Kick off analytics on mount
  useEffect(() => {
    if (sessionInitialized && analyticsState === 'loading') {
      analyticsCoordinator
        .on('ready', () => setAnalyticsState('waiting'))
        .on('finished', () => {
          setAnalyticsState('done');

          // Marketing pixels are only initialized when the user has granted targeting consent.
          // analytics.js itself is already gated on consent in index.html, so by the time we
          // reach this callback consent should be granted — this is a defensive check.
          if (!hasTargetingConsent()) {
            return;
          }

          // Initialize a Facebook Pixel separate from analytics.js integration. It will fire to a separate
          // pixel ID and will live side-by-side with the pixel ID that is configured with analytics.js.
          fbInit();

          // Northbeam pixel integration
          northbeamInit();

          // Identify the user in Hotjar using the Segment anonymous ID so we can look up
          // session recordings by the same identifier used across our analytics stack.
          if (window.analytics && typeof window.analytics.user === 'function') {
            const anonymousId = window.analytics.user().anonymousId();
            if (anonymousId) {
              hotjar.identify(anonymousId);
            }
          }
        })
        .start();
    }
  }, [analyticsState, sessionInitialized]);

  // Navigation hooks (before page change, after page change)
  const [handledLocation, setHandledLocation] = useState(location);
  if (location !== handledLocation) {
    analyticsCoordinator.beforePageChange();
    setHandledLocation(location);
  }
  // Only fire pageChange() *after* component is mounted.
  // https://stackoverflow.com/a/53351556/1480571
  const mounted = useRef(false);
  useEffect(() => {
    if (mounted.current) {
      analyticsCoordinator.pageChange();
    } else {
      mounted.current = true;
    }
  }, [location]);

  return { analyticsState };
}
